import { Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { IUserInfo } from '@ruby/modules/account/models/contact.interface';
import { ICustomer, ILocation } from '@ruby/modules/customer-select/models/customer.interface';
import { IThirdPartyEcr, IThirdPartyLineOfBusiness, IThirdPartyLOA } from '@ruby/modules/third-party/models/third-party.interface';
import { selectThirdPartyPreviewState } from '@ruby/modules/third-party/store';
import { fromThirdPartyAuthorization } from '@ruby/modules/third-party/store/third-party-authorization.index';
import { fromThirdPartyCompanies } from '@ruby/modules/third-party/store/third-party-companies.index';
import { fromThirdPartyCore, ThirdPartyCoreActions } from '@ruby/modules/third-party/store/third-party-core.index';
import { fromThirdPartyStepper } from '@ruby/modules/third-party/store/third-party-stepper.index';
import { ERole } from '@ruby/shared/enums/roles.enums';
import { UtilsService } from '@ruby/shared/services/utils/utils.service';

@Component({
  selector: 'ng-third-party-preview-container',
  templateUrl: './third-preview.container.html'
})
export class ThirdPartyPreviewContainerComponent implements OnInit {
  contract?: IThirdPartyLOA;
  contractToSign!: IThirdPartyLOA;
  lob?: IThirdPartyLineOfBusiness;
  auth!: fromThirdPartyAuthorization.State;
  companies!: fromThirdPartyCompanies.State;
  core!: fromThirdPartyCore.State;
  userInfo!: IUserInfo;
  isEdit!: boolean;
  store$: Observable<{
    core: fromThirdPartyCore.State;
    companies: fromThirdPartyCompanies.State;
    authorizations: fromThirdPartyAuthorization.State;
    stepper: fromThirdPartyStepper.State;
    userInfo: IUserInfo;
  }>;

  constructor(private store: Store<fromThirdPartyCore.State>, private utilsSvc: UtilsService) {
    this.store$ = this.store.select(selectThirdPartyPreviewState).pipe(
      tap((st: {
        core: fromThirdPartyCore.State;
        companies: fromThirdPartyCompanies.State;
        authorizations: fromThirdPartyAuthorization.State;
        stepper: fromThirdPartyStepper.State;
        userInfo: IUserInfo;
      }) => {
        this.isEdit = st.stepper.isEdit;
        this.auth = st.authorizations;
        this.companies = st.companies;
        this.lob = st.authorizations.lob.find((lob: IThirdPartyLineOfBusiness) => lob.role === st.authorizations.selectedLOB);
        this.userInfo = st.userInfo;
        this.core = st.core;
        this.setupContract();
        this.previewContract();
      })
    );
  }

  ngOnInit(): void {
    this.store.dispatch(ThirdPartyCoreActions.loadTemplate());
  }

  /**
   * Sign
   *
   * @summary The following method dispatch an action to create/edit an LOA
   * @returns void
   */
  sign(): void {
    if (this.isEdit && this.core.loa && this.core.loa.id) {
      this.store.dispatch(ThirdPartyCoreActions.updateLOA({ data: { id: this.core.loa.id, contract: this.contractToSign } }));
    } else {
      this.store.dispatch(ThirdPartyCoreActions.signLOA({ data: this.contractToSign }));
    }
  }

  /**
   * Setup Contract
   *
   * @summary The following method fill the initial values to sign an LOA
   * @returns void
   */
  setupContract(): void {
    this.contractToSign = {
      apEmptyCarRequest: this.auth.apEcr,
      apHljs: this.auth.apHljs,
      apInventoryManagement: false,
      apRating: this.auth.apRating,
      apRevenue: this.auth.apRevenue,
      apRevenueClaims: this.auth.apRevenueClaims,
      apStorageAndLift: this.auth.apStorageAndLift,
      apTransportation: this.auth.apTransp,
      companyEid: this.companies.customerCompany ? this.companies.customerCompany.customerIdentifier.padEnd(10, '0') : '',
      companyTPLEid: this.companies.providerCompany ? this.companies.providerCompany.eid.padEnd(10, '0') : '',
      lineOfBusiness: this.lob ? this.lob.lineOfBusiness : '',
      locations: this.getLocations(),
      ecrDetails: this.getEcrData()
    };
  }

  /**
   * Preview Contract
   *
   * @summary The following method fills the remaining information for preview the contract
   * @returns void
   */
  previewContract(): void {
    if (this.userInfo && this.userInfo.racfId) {
      this.contract = {
        ...this.contractToSign,
        contractId: '',
        companyName: this.companies.customerCompany ? this.companies.customerCompany.customerName : '',
        companyTPLName: this.companies.providerCompany ? this.companies.providerCompany.name : '',
        id: '',
        lineOfBusiness: this.auth.selectedLOB ? this.utilsSvc.mappingRoleToTitle(this.auth.selectedLOB as ERole) : '',
        status: '',
        updatedBy: '',
        updatedByName: '',
        contractContent: this.core.contractTemplate?.contractText,
        createdBy: this.userInfo.racfId,
        createdByName: `${ this.userInfo.contact.firstName } ${ this.userInfo.contact.lastName }`
      };
      if (this.isEdit && this.core.loa) {
        this.contract.startDate = new Date().getTime();
        this.contract.endDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).getTime();
        this.contract.createdAt = this.core.loa.createdAt;
      } else if (!this.isEdit && this.core.loa) {
        this.contract.startDate = this.core.loa.startDate;
        this.contract.endDate = this.core.loa.endDate;
        this.contract.createdAt = this.core.loa.createdAt;
      } else {
        this.contract.endDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).getTime();
        this.contract.startDate = new Date().getTime();
        this.contract.createdAt = new Date().getTime();
      }
    }
  }

  private getLocations(): Array<ILocation> {
    if (this.auth.locationsByLOB && this.auth.locationsByLOB[this.utilsSvc.getTypeRoleMapping(this.auth.selectedLOB as ERole)]) {
      const customer: ICustomer = this.auth.locationsByLOB[this.utilsSvc.getTypeRoleMapping(this.auth.selectedLOB as ERole)].customer;
      return customer.locations;
    }
    return [];
  }

  private getEcrData(): IThirdPartyEcr | undefined {
    if (this.auth.ecrData) {
      return {
        equipmentTypes: this.auth.ecrData.equipmentTypes,
        railRoadCompanyEId: this.auth.ecrData.railRoadCompany.key,
        railRoadLocState: this.auth.ecrData.originState,
        railRoadLocCity: this.auth.ecrData.originCity.key,
        stcc: this.auth.ecrData.stccDescription.key
      };
    }
    return undefined;
  }
}
